import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Github, Loader2, Lock, Plug, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { useOrgs } from "@/lib/orgs";
import { RelativeTime } from "@/components/RelativeTime";

interface RepoSummary {
  owner: string;
  name: string;
  fullName: string;
  private: boolean;
  defaultBranch: string;
  description: string | null;
  pushedAt: string | null;
}

interface DetectedApp {
  path: string;
  framework: "vite" | "next";
  packageManager: "pnpm" | "yarn" | "npm";
  nodeVersion: string;
}

export function Import() {
  const orgs = useOrgs();
  const org = orgs.data?.[0];
  const qc = useQueryClient();
  const nav = useNavigate();
  const [pat, setPat] = useState("");
  const [savingPat, setSavingPat] = useState(false);
  const [patErr, setPatErr] = useState<string | null>(null);
  const [selectedRepo, setSelectedRepo] = useState<RepoSummary | null>(null);
  const [scanResult, setScanResult] = useState<{ defaultBranch: string; apps: DetectedApp[] } | null>(null);
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [repoFilter, setRepoFilter] = useState("");
  const [publicRepoInput, setPublicRepoInput] = useState("");
  const [publicRepoErr, setPublicRepoErr] = useState<string | null>(null);

  const tokenStatus = useQuery({
    queryKey: ["github-token"],
    queryFn: () => api.get<{ connected: boolean; scopes: string[] }>("/github/token"),
  });

  const repos = useQuery({
    enabled: !!tokenStatus.data?.connected,
    queryKey: ["github-repos"],
    queryFn: async () => {
      const r = await api.get<{ repos: RepoSummary[] }>("/github/repos?perPage=50");
      return r.repos;
    },
  });

  async function savePat() {
    setPatErr(null);
    setSavingPat(true);
    try {
      await api.post("/github/token", { pat });
      qc.invalidateQueries({ queryKey: ["github-token"] });
      qc.invalidateQueries({ queryKey: ["github-repos"] });
      setPat("");
    } catch (e) {
      setPatErr(e instanceof ApiError ? e.message : "Failed to verify token");
    } finally {
      setSavingPat(false);
    }
  }

  const scanMut = useMutation({
    mutationFn: async (repo: string) => api.post<{ defaultBranch: string; private: boolean; apps: DetectedApp[] }>("/github/scan", { repo }),
    onSuccess: (data) => {
      setScanResult(data);
      setSelectedApps(new Set(data.apps.map((a) => a.path)));
    },
  });

  /** Accepts "owner/repo" or a github.com URL (with or without .git). */
  function parseRepoInput(raw: string): string | null {
    const s = raw.trim().replace(/\.git$/, "");
    const m = s.match(/^(?:https?:\/\/(?:www\.)?github\.com\/)?([\w.-]+)\/([\w.-]+?)\/?$/);
    if (!m) return null;
    return `${m[1]}/${m[2]}`;
  }

  function importPublicRepo() {
    const fullName = parseRepoInput(publicRepoInput);
    if (!fullName) {
      setPublicRepoErr("Enter a repo as owner/name or a github.com URL");
      return;
    }
    setPublicRepoErr(null);
    const [owner, name] = fullName.split("/") as [string, string];
    setSelectedRepo({ owner, name, fullName, private: false, defaultBranch: "", description: null, pushedAt: null });
    scanMut.mutate(fullName);
  }

  // Step after create: env-var paste. Holds the slugs of just-created
  // projects so the user can paste vars then trigger the first deploy.
  const [created, setCreated] = useState<{ slugs: string[] } | null>(null);
  const [envText, setEnvText] = useState("");
  const [envSaveError, setEnvSaveError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: async () => {
      if (!org || !selectedRepo || !scanResult) throw new Error("missing context");
      const apps = scanResult.apps.filter((a) => selectedApps.has(a.path));
      if (apps.length === 1) {
        const a = apps[0]!;
        const r = await api.post<{ project: { slug: string } }>(`/orgs/${org.slug}/projects`, {
          slug: makeSlug(selectedRepo.name, a.path),
          githubRepo: selectedRepo.fullName,
          rootDirectory: a.path,
          prodBranch: scanResult.defaultBranch,
          framework: a.framework,
          packageManager: a.packageManager,
          nodeVersion: a.nodeVersion,
        });
        return { slugs: [r.project.slug] };
      }
      const r = await api.post<{ projects: { slug: string }[] }>(
        `/orgs/${org.slug}/projects/bulk`,
        {
          githubRepo: selectedRepo.fullName,
          apps: apps.map((a) => ({
            slug: makeSlug(selectedRepo.name, a.path),
            rootDirectory: a.path,
            prodBranch: scanResult.defaultBranch,
            framework: a.framework,
            packageManager: a.packageManager,
            nodeVersion: a.nodeVersion,
          })),
        },
      );
      return { slugs: r.projects.map((p) => p.slug) };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["projects", org?.slug] });
      setCreated(data);
    },
  });

  // "Save env + Deploy" — for each created project, upsert env vars (only
  // if user pasted any) then trigger a production redeploy. Single-project
  // path navigates to live logs; multi-project bulk lands on the dash.
  const deployMut = useMutation({
    mutationFn: async () => {
      if (!org || !created) throw new Error("missing context");
      const parsed = parseEnvBlob(envText);
      setEnvSaveError(null);
      const firstDeployments: { slug: string; deploymentId: string }[] = [];
      for (const slug of created.slugs) {
        if (parsed.length > 0) {
          await api.post(`/orgs/${org.slug}/projects/${slug}/env-vars/bulk`, {
            vars: parsed.map((v) => ({
              key: v.key,
              value: v.value,
              // Apply to all envs at import time so PR previews + staging
              // builds inherit the same config as production. Per-env
              // overrides land in US4 (Phase 6).
              environments: ["production", "staging", "preview", "development"],
              isSecret: true,
            })),
          });
        }
        const r = await api.post<{ deploymentId: string }>(
          `/orgs/${org.slug}/projects/${slug}/redeploy`,
          { env: "production" },
        );
        firstDeployments.push({ slug, deploymentId: r.deploymentId });
      }
      return firstDeployments;
    },
    onSuccess: (deployments) => {
      qc.invalidateQueries({ queryKey: ["projects", org?.slug] });
      if (deployments.length === 1) {
        const d = deployments[0]!;
        nav(`/orgs/${org!.slug}/projects/${d.slug}/deployments/${d.deploymentId}`);
      } else {
        nav(`/orgs/${org!.slug}`);
      }
    },
    onError: (e) => {
      setEnvSaveError(e instanceof ApiError ? e.message : "save failed");
    },
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6">
      <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Back to projects</Link>
      <h1 className="text-xl font-semibold tracking-tight mt-2">Import a Git repository</h1>
      <p className="text-sm text-muted-foreground">
        Connect a GitHub repo. We auto-detect Vite and Next.js apps; non-matching repos are refused.
      </p>

      {!scanResult && !created && (
        <div className="of-card mt-6 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Github className="h-4 w-4" />
            <h2 className="text-sm font-semibold">Import any public repo</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Paste an <span className="font-mono">owner/name</span> or a github.com URL — no GitHub connection needed
            for public repos. Auto-deploy on push requires connecting GitHub below.
          </p>
          <div className="flex items-center gap-2">
            <Input
              value={publicRepoInput}
              onChange={(e) => setPublicRepoInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && importPublicRepo()}
              placeholder="vercel/next.js or https://github.com/vercel/next.js"
              className="font-mono text-sm"
            />
            <Button
              variant="default"
              disabled={!publicRepoInput.trim() || scanMut.isPending}
              onClick={importPublicRepo}
            >
              {scanMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Import
            </Button>
          </div>
          {publicRepoErr && <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{publicRepoErr}</div>}
          {scanMut.error instanceof ApiError && (
            <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{scanMut.error.message}</div>
          )}
        </div>
      )}

      {!tokenStatus.data?.connected ? (
        <div className="of-card mt-6 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Github className="h-4 w-4" />
            <h2 className="text-sm font-semibold">Connect GitHub</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Needed to browse your private repos and to enable auto-deploy on push. Not required to import a public repo by URL above.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="pat">Personal access token (classic)</Label>
            <Input id="pat" type="password" value={pat} onChange={(e) => setPat(e.target.value)} placeholder="ghp_…" />
            <p className="text-2xs text-subtle-foreground">
              Scopes: <span className="font-mono">repo</span>, <span className="font-mono">read:user</span>. Stored encrypted at rest.
            </p>
          </div>
          {patErr && <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{patErr}</div>}
          <Button variant="primary" disabled={!pat || savingPat} onClick={savePat}>
            {savingPat ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />} Connect
          </Button>
        </div>
      ) : null}

      {tokenStatus.data?.connected && !scanResult && (() => {
        const all = repos.data ?? [];
        const q = repoFilter.trim().toLowerCase();
        const filtered = q
          ? all.filter((r) =>
              r.fullName.toLowerCase().includes(q) ||
              (r.description ?? "").toLowerCase().includes(q),
            )
          : all;
        return (
          <div className="of-card mt-6">
            <div className="of-card-header flex-wrap gap-2 gap-y-2">
              <h2 className="text-sm font-semibold">Pick a repository</h2>
              <div className="flex items-center gap-2 w-full max-w-md ml-auto sm:w-auto sm:flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-subtle-foreground pointer-events-none" />
                  <Input
                    type="search"
                    placeholder="Filter repos by name or description…"
                    value={repoFilter}
                    onChange={(e) => setRepoFilter(e.target.value)}
                    className="pl-7 h-7 text-sm"
                  />
                </div>
                <span className="text-2xs text-subtle-foreground whitespace-nowrap font-mono">
                  {q ? `${filtered.length} / ${all.length}` : `${all.length} repos`}
                </span>
              </div>
            </div>
            {repos.isLoading && <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading repos…</div>}
            {!repos.isLoading && filtered.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                {q ? <>No repos match <span className="font-mono">{repoFilter}</span>.</> : "No repos found."}
              </div>
            )}
            {filtered.map((r) => (
              <button
                key={r.fullName}
                onClick={() => {
                  setSelectedRepo(r);
                  scanMut.mutate(r.fullName);
                }}
                className="of-row w-full text-left min-h-[52px]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-semibold font-mono truncate">{r.fullName}</span>
                    {r.private && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
                  </div>
                  {r.description && <div className="text-xs text-muted-foreground truncate-1">{r.description}</div>}
                </div>
                <div className="w-24 shrink-0 text-right text-xs text-subtle-foreground">
                  {r.pushedAt && <RelativeTime value={r.pushedAt} />}
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-subtle-foreground shrink-0" />
              </button>
            ))}
          </div>
        );
      })()}

      {scanMut.isPending && (
        <div className="of-card mt-6 px-4 py-10 text-center text-sm text-muted-foreground inline-flex items-center justify-center w-full gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Scanning {selectedRepo?.fullName}…
        </div>
      )}

      {created && (
        <div className="of-card mt-6">
          <div className="of-card-header flex-wrap gap-y-1">
            <h2 className="text-sm font-semibold">
              Add production env vars
              <span className="ml-2 text-2xs font-mono text-subtle-foreground">
                {created.slugs.length} project{created.slugs.length === 1 ? "" : "s"}
              </span>
            </h2>
            <span className="text-2xs text-subtle-foreground">optional — skip to deploy with no vars</span>
          </div>
          <div className="px-4 py-3 space-y-2">
            <Label htmlFor="env-blob" className="text-xs">Paste <span className="font-mono">.env</span> contents</Label>
            <textarea
              id="env-blob"
              value={envText}
              onChange={(e) => setEnvText(e.target.value)}
              placeholder={"DATABASE_URL=postgres://…\nAPI_KEY=sk-…\n# comments and blank lines ignored"}
              spellCheck={false}
              className="w-full min-h-[140px] font-mono text-xs bg-subtle border border-border rounded-md p-2.5 placeholder:text-subtle-foreground/60 focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <p className="text-2xs text-subtle-foreground">
              Values are AES-256-GCM encrypted at rest. Scoped to <span className="font-mono">production</span>; staging/preview/dev can be set later from the project Environments tab.
            </p>
            {envSaveError && (
              <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
                {envSaveError}
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t border-border-muted flex items-center justify-between bg-subtle/40">
            <span className="text-xs text-muted-foreground">
              {(() => {
                const n = parseEnvBlob(envText).length;
                return n === 0 ? "no vars" : `${n} var${n === 1 ? "" : "s"} parsed`;
              })()}
            </span>
            <Button
              variant="primary"
              disabled={deployMut.isPending}
              onClick={() => deployMut.mutate()}
            >
              {deployMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {parseEnvBlob(envText).length > 0 ? "Save & Deploy" : "Deploy"}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {scanResult && !created && (
        <div className="of-card mt-6">
          <div className="of-card-header gap-2">
            <h2 className="text-sm font-semibold min-w-0 truncate">
              Detected apps in <span className="font-mono">{selectedRepo?.fullName}</span>
            </h2>
            <span className="text-2xs text-subtle-foreground shrink-0">{scanResult.apps.length} detected</span>
          </div>
          {scanResult.apps.length === 0 && (
            <div className="px-4 py-8 text-center">
              <div className="text-sm text-danger">No Vite or Next.js apps detected.</div>
              <div className="text-xs text-muted-foreground mt-1">
                openfront supports Vite and Next.js only. Add one of them as a dependency in <span className="font-mono">package.json</span> and rescan.
              </div>
            </div>
          )}
          {scanResult.apps.map((a) => (
            <label key={a.path} className="of-row cursor-pointer">
              <input
                type="checkbox"
                checked={selectedApps.has(a.path)}
                onChange={(e) => {
                  const next = new Set(selectedApps);
                  e.target.checked ? next.add(a.path) : next.delete(a.path);
                  setSelectedApps(next);
                }}
                className="h-3.5 w-3.5 accent-[rgb(var(--of-accent-emphasis))]"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm break-all">{a.path === "/" ? "(repo root)" : a.path}</span>
                  <span className="text-2xs uppercase tracking-wider text-subtle-foreground font-mono">{a.framework}</span>
                  <span className="text-2xs uppercase tracking-wider text-subtle-foreground font-mono">{a.packageManager}</span>
                  <span className="text-2xs text-subtle-foreground font-mono">node {a.nodeVersion}</span>
                </div>
              </div>
              <CheckCircle2 className={selectedApps.has(a.path) ? "h-4 w-4 text-success" : "h-4 w-4 text-subtle-foreground opacity-30"} />
            </label>
          ))}
          {scanResult.apps.length > 0 && (
            <div className="px-4 py-3 border-t border-border-muted flex items-center justify-between bg-subtle/40">
              <span className="text-xs text-muted-foreground">{selectedApps.size} selected</span>
              <Button
                variant="primary"
                disabled={selectedApps.size === 0 || createMut.isPending}
                onClick={() => createMut.mutate()}
              >
                {createMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Create {selectedApps.size} project{selectedApps.size === 1 ? "" : "s"}
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          {createMut.error instanceof ApiError && (
            <div className="mx-4 mb-3 text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
              {createMut.error.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function makeSlug(repoName: string, path: string): string {
  if (path === "/") return slug(repoName);
  const suffix = path.replace(/^\/+/, "").replace(/\//g, "-");
  return slug(`${repoName}-${suffix}`);
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-").replace(/^-+|-+$/g, "");
}

/**
 * Parse a pasted .env blob into {key, value} pairs. Skips blank lines and
 * `#` comments. Accepts optional `export ` prefix and single/double-quoted
 * values. Rejects entries with invalid names (must start with letter or
 * underscore, then alphanumerics/underscores).
 */
function parseEnvBlob(text: string): { key: string; value: string }[] {
  const out: { key: string; value: string }[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.replace(/^\s*export\s+/, "").trim();
    if (!line || line.startsWith("#")) continue;
    const eq = line.indexOf("=");
    if (eq <= 0) continue;
    const key = line.slice(0, eq).trim();
    let value = line.slice(eq + 1).trim();
    if (!/^[A-Z_][A-Z0-9_]*$/i.test(key)) continue;
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    out.push({ key, value });
  }
  return out;
}
